/* Shared video-embed helpers used by athlete pages, brand network pages,
 * and any future surface that lets admins paste arbitrary video URLs.
 * Extracted from AthleteNetworkPage so every consumer behaves the same
 * way for the same URL.
 *
 * Accepts any YouTube / Vimeo / Instagram URL the user might paste from
 * mobile / desktop / share sheet and returns the canonical iframe-ready
 * embed URL. Returns null if the URL isn't a recognized video link —
 * caller should then fall through to a raw <video> tag (works for MP4s,
 * /api/files/ uploads, direct CDN links, etc.).
 *   YouTube  : watch, share (youtu.be), Shorts, /live/, /embed/
 *   Vimeo    : vimeo.com/<id>, /<id>/<hash> (private), channels/, groups/,
 *              event/, album/, showcase/, player.vimeo.com/video/
 *   Instagram: /p/<code>/, /reel/<code>/, /reels/<code>/, /tv/<code>/
 */
export const toVideoEmbed = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const url = raw.trim();

  // ── YouTube ──────────────────────────────────────────────
  if (/youtube\.com|youtu\.be/i.test(url)) {
    if (/youtube(-nocookie)?\.com\/embed\//i.test(url)) return url;
    let id = null;
    const m1 = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    const m2 = url.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
    const m3 = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/);
    const m4 = url.match(/youtube\.com\/live\/([A-Za-z0-9_-]{6,})/);
    if (m1) id = m1[1];
    else if (m2) id = m2[1];
    else if (m3) id = m3[1];
    else if (m4) id = m4[1];
    // iter344 · Use youtube-nocookie + playsinline so the embed loads
    // reliably inside the iOS Capacitor webview (regular youtube.com
    // embeds surfaced YouTube's "Video player configuration error"
    // when the origin is capacitor://).
    if (id) return `https://www.youtube-nocookie.com/embed/${id}?playsinline=1&rel=0&modestbranding=1&iv_load_policy=3`;
    return null;
  }

  // ── Vimeo ────────────────────────────────────────────────
  if (/vimeo\.com/i.test(url)) {
    if (/player\.vimeo\.com\/video\//i.test(url)) return url;
    const priv = url.match(/vimeo\.com\/(\d{6,})\/([A-Za-z0-9]+)/);
    if (priv) return `https://player.vimeo.com/video/${priv[1]}?h=${priv[2]}`;
    const ids = url.match(/(\d{6,})/g);
    const id = ids ? ids[ids.length - 1] : null;
    if (id) return `https://player.vimeo.com/video/${id}`;
    return null;
  }

  // ── Instagram ────────────────────────────────────────────
  // Reels, posts and IGTV all support the same `/embed/` suffix once we
  // strip the trailing slash + query/tracking params.
  if (/instagram\.com/i.test(url)) {
    const m = url.match(/instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
    if (!m) return null;
    return `https://www.instagram.com/p/${m[1]}/embed/`;
  }

  return null;
};

// Picks the right Tailwind aspect class for a given source URL. YouTube /
// Vimeo / MP4 stay 16:9; Instagram reels and tv flip to 9:16 (portrait);
// Instagram feed posts default to 4:5 since most are portrait now.
export const getVideoAspectClass = (raw) => {
  if (!raw || typeof raw !== 'string') return 'aspect-video';
  const url = raw.trim();
  if (/instagram\.com\/(reel|reels|tv)\//i.test(url)) return 'aspect-[9/16] max-w-[420px] mx-auto';
  if (/instagram\.com\/p\//i.test(url)) return 'aspect-[4/5] max-w-[460px] mx-auto';
  return 'aspect-video';
};

// True when the raw URL points at a direct video file (MP4/WebM/MOV) or
// our own /api/files/ upload pipeline — caller renders <video> instead
// of an iframe so the browser controls the playback natively.
export const isDirectVideo = (raw) => {
  if (!raw || typeof raw !== 'string') return false;
  const url = raw.trim().toLowerCase();
  if (url.startsWith('/api/files/')) return true;
  return /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i.test(url);
};
// Hudl profile/watch pages block iframe embedding and aren't a direct
// video file either (they're an HTML page) — a caller that fell through
// to a raw <video src="hudl.com/..."> would render the browser's broken-
// media icon forever. Callers should check this (or just !isDirectVideo)
// and render a tap-through "Watch on Hudl" link instead.
export const isHudlUrl = (raw) => !!raw && typeof raw === 'string' && /hudl\.com/i.test(raw);

/**
 * ytEmbedUrl — canonical YouTube embed URL builder.
 * Uses youtube-nocookie.com + playsinline so the embed loads reliably
 * inside iOS Capacitor / TestFlight webviews (regular youtube.com
 * embeds trigger YouTube's "Video player configuration error / 153"
 * when the origin is capacitor://). Caller can pass `{ autoplay: true }`
 * for lightbox/modal players that should start playing immediately.
 */
export const ytEmbedUrl = (id, { autoplay = false } = {}) => {
  if (!id) return '';
  const params = ['playsinline=1', 'rel=0', 'modestbranding=1', 'iv_load_policy=3'];
  if (autoplay) params.push('autoplay=1');
  return `https://www.youtube-nocookie.com/embed/${id}?${params.join('&')}`;
};
